import React, { useEffect, useMemo } from 'react';

import { ItemRowActionsMenu } from '@shared/components';
import { TItem } from '@modules/items';
import {
  ECountriesFolderType,
  EFolderType,
  ESnapshotsFolderType,
  IFolder,
  TOnMove,
  TPathItem,
} from '../types/folders.types';
import { TItemsTableRow, TAction, TColumnLabels, TShowColumn, TResolvedItemNames } from '../types/items.types';
import { AccessType } from '@modules/access/types';
import ItemsTable from './ItemsTable/ItemsTable';
import { useSelectableTable } from '@shared/hooks';
import {
  ITEMS_TABLE_COLUMN_IDS,
  SINGLE_SNAPSHOT_TABLE_COLUMN_IDS,
  LIBRARY_TABLE_COLUMN_IDS,
  COUNTRIES_OR_SNAPSHOTS_ROOT_TABLE_COLUMN_IDS,
  COUNTRIES_OR_SNAPSHOTS_SUB_TABLE_COLUMN_IDS,
} from '../constants';
import { TERMINOLOGY } from '@shared/i18n';

type TProps = {
  folder: IFolder;
  items: TItem[];
  selectedItemIds: Set<string>;
  setSelectedItemIds: (selectedItemIds: Set<string>) => void;
  actions: TAction[];
  id?: string;
  resolvedNames: TResolvedItemNames;
  fullPath: TPathItem[];
  isBlockFolderType: boolean;
  countriesFolderType: ECountriesFolderType | undefined;
  snapshotsFolderType: ESnapshotsFolderType | undefined;
  onMove: TOnMove;
};

const ActionItemsTable = ({
  folder,
  items,
  selectedItemIds,
  setSelectedItemIds,
  actions,
  id,
  resolvedNames,
  isBlockFolderType,
  countriesFolderType,
  snapshotsFolderType,
  onMove,
}: TProps) => {
  useEffect(() => {
    setSelectedItemIds(new Set<string>());
  }, [id]);

  let columnIds = ITEMS_TABLE_COLUMN_IDS;
  let show: TShowColumn = {};
  const labels: TColumnLabels = {};
  const isWithinSingleSnapshot = folder.folderType === EFolderType.SNAPSHOT && !snapshotsFolderType;

  // database:
  if (isBlockFolderType) {
    columnIds = LIBRARY_TABLE_COLUMN_IDS;
    show = { preview: true, approvedVersion: false, status: false };
    labels.version = 'Version';
  }

  // country registrations:
  if (countriesFolderType) {
    if (countriesFolderType === ECountriesFolderType.ROOT) {
      // root view
      columnIds = COUNTRIES_OR_SNAPSHOTS_ROOT_TABLE_COLUMN_IDS;
      show = { status: false, approvedVersion: false, version: false };
      labels.name = TERMINOLOGY.registrationCountry.upper;
    } else if (countriesFolderType === ECountriesFolderType.COUNTRY) {
      // country view
      columnIds = COUNTRIES_OR_SNAPSHOTS_SUB_TABLE_COLUMN_IDS;
      show = { status: false, approvedVersion: false, version: false, products: true };
      labels.name = TERMINOLOGY.registrationSingle.upper;
    }
    // else normal FS view
  }

  // snapshots:
  if (snapshotsFolderType) {
    if (snapshotsFolderType === ESnapshotsFolderType.ROOT) {
      // root view
      columnIds = COUNTRIES_OR_SNAPSHOTS_ROOT_TABLE_COLUMN_IDS;
      show = { status: false, approvedVersion: false, version: false };
      labels.name = TERMINOLOGY.snapshotCollection.upper;
    } else if (snapshotsFolderType === ESnapshotsFolderType.SNAPSHOT_COLLECTION) {
      // snapshot collection view
      columnIds = COUNTRIES_OR_SNAPSHOTS_SUB_TABLE_COLUMN_IDS;
      show = { status: false, approvedVersion: false, version: false };
      labels.name = TERMINOLOGY.snapshot.upper;
    }
    // else normal FS view
  }

  if (isWithinSingleSnapshot) {
    columnIds = SINGLE_SNAPSHOT_TABLE_COLUMN_IDS;
    show = { version: true, approvedVersion: false };
  }

  // enable / disable drag-and-drop move feature:
  const canMove =
    folder.accessRight >= AccessType.EDIT &&
    !countriesFolderType && // is undefined within a single country registration (or outside the countries space)
    !snapshotsFolderType; // is undefined within a single snapshot (or outside the snapshots space)

  const { selectionHeaderCell, tableRows, columnSizing } = useSelectableTable(
    columnIds,
    items,
    (item) => item._id,
    selectedItemIds,
    setSelectedItemIds,
    {
      resizable: true,
      tableId: 'action-items-table',
      columnSizingParams: {
        autoFitColumns: false,
        columnSizingOptions: {
          name: { idealWidth: 300, minWidth: 150 },
          products: { defaultWidth: 200 },
          preview: { idealWidth: 200, minWidth: 100 },
          modified: { defaultWidth: 75 },
          latestVersionId: { defaultWidth: 75 },
          approvedVersionId: { defaultWidth: 75 },
          status: { defaultWidth: 75 },
          authors: { defaultWidth: 75 },
        },
      },
    }
  );

  const itemTableRows: TItemsTableRow[] = useMemo(
    () =>
      tableRows.map((row) => ({
        ...row,
        /* action pill ----------------------------------------------------- */
        actionButton: (
          <ItemRowActionsMenu
            item={row.item}
            actions={actions}
            triggerClassName={row.actionButtonClassName}
            open={row.menu?.isOpen}
            onOpenChange={row.menu?.onOpenChange}
            alwaysVisible={row.alwaysVisible}
          />
        ),
        /* delegate right-click to the hook-generated handler */
        onContextMenu: row.menu?.onContextMenu,
      })),
    [tableRows]
  );

  const handleLinkClick = (e: React.MouseEvent<HTMLAnchorElement>) => e.stopPropagation();

  return (
    <ItemsTable
      columnSizing={columnSizing}
      rows={itemTableRows}
      selectionHeaderCell={selectionHeaderCell}
      onLinkClick={handleLinkClick}
      onMove={canMove ? onMove : undefined}
      labels={labels}
      show={show}
      resolvedNames={resolvedNames}
      folderType={folder.folderType}
    />
  );
};

export default ActionItemsTable;
